import { CheckCircle2, Clock, Loader } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { RoadmapBucket } from "./roadmap";

const STATS: { key: RoadmapBucket; label: string; icon: typeof Clock; tone: string }[] = [
	{ key: "now", label: "Working on now", icon: Loader, tone: "text-warning bg-warning-subtle" },
	{ key: "next", label: "Coming up next", icon: Clock, tone: "text-primary bg-primary-subtle" },
	{ key: "done", label: "Done", icon: CheckCircle2, tone: "text-secondary bg-secondary-subtle" },
];

export function PublicRoadmapSummary({
	counts,
	loading,
}: {
	counts: Record<RoadmapBucket, number>;
	loading: boolean;
}) {
	if (loading) return <Skeleton className="mb-6 h-36 w-full rounded" />;

	const total = counts.now + counts.next + counts.done;
	const percent = total ? Math.round((counts.done / total) * 100) : 0;

	return (
		<Card className="mb-6 flex flex-col gap-5 p-5">
			<div>
				<div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3">
					<p className="text-sm font-medium text-foreground">Overall progress</p>
					<p className="text-sm text-muted-foreground">
						<span className="text-2xl font-semibold text-foreground">{percent}%</span>{" "}
						complete ({counts.done} of {total} tasks)
					</p>
				</div>
				<div
					role="progressbar"
					aria-valuenow={percent}
					aria-valuemin={0}
					aria-valuemax={100}
					aria-label="Overall progress"
					className="h-2.5 overflow-hidden rounded-full bg-muted-subtle"
				>
					<div
						className="h-full rounded-full bg-secondary transition-[width]"
						style={{ width: `${percent}%` }}
					/>
				</div>
			</div>

			<div className="grid grid-cols-3 gap-2 sm:gap-3">
				{STATS.map(({ key, label, icon: StatIcon, tone }) => (
					<div
						key={key}
						className="flex items-center gap-3 rounded border border-border p-2.5 sm:p-3"
					>
						<span className={`hidden h-9 w-9 shrink-0 items-center justify-center rounded-full sm:flex ${tone}`}>
							<StatIcon className="h-4 w-4" />
						</span>
						<div>
							<p className="text-xl font-semibold leading-none text-foreground">
								{counts[key]}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">{label}</p>
						</div>
					</div>
				))}
			</div>
		</Card>
	);
}
